import Consts from '@/api/Consts';

import {
	HEATMAP_OK_COLOR,
	HEATMAP_SINGLE_COLOR,
	HEATMAP_MULTI_COLOR,
} from '@/utils/spoofingVisibilityHeatmapUtils';
import type { HeatmapTooltipRow } from '@/utils/spoofingVisibilityHeatmapUtils';


export type BrdcmCoverClass = 'quorum' | 'held' | 'weak';

export type BrdcmMergeItem = {
	current_datetime: string;
	merge_id: number;
	status: string;
};

export type BrdcmStatsItem = {
	current_datetime: string;
	satellite: string;
	cover_class: BrdcmCoverClass | null;
	stations_count: number | null;
	quorum_count: number | null;
	stations: string[];
	msm_stations: string[];
};

export type BrdcmHeatmapChartPointItem = {
	current_datetime: string;
	satellite: string;
	status: number | null;
	tooltipRows: HeatmapTooltipRow[];
};

export type BrdcmHeatmapTooltipLabels = {
	stations: string;
	minimum: string;
	mergeId: string;
	ephStations: string;
	msmStations: string;
};

export const BRDCM_STATUS_OK = 0;
export const BRDCM_STATUS_SINGLE = 50;
export const BRDCM_STATUS_HELD = 25;
export const BRDCM_STATUS_MULTI = 100;
export const BRDCM_HELD_COLOR = '1890ff';

export const BRDCM_GNSS_CODES = Consts.sortGnssCodes(
	Consts.GNSS_DATA.filter(item => item.isActual).map(item => item.code),
);


const parseSatNumber = (sat: string): number => {
	const numericPart = sat.slice(1);
	const parsed = Number.parseInt(numericPart, 10);
	return Number.isNaN(parsed) ? 0 : parsed;
};


export const sortSatLabels = (sats: readonly string[]): string[] => {
	return [...sats].sort((left, right) => {
		const leftGnss = left[0] || '';
		const rightGnss = right[0] || '';
		if (leftGnss !== rightGnss) {
			return leftGnss.localeCompare(rightGnss);
		}
		return parseSatNumber(left) - parseSatNumber(right);
	});
};


export const filterSatsByGnss = (sats: readonly string[], selectedGnss: readonly string[]): string[] => {
	if (selectedGnss.length === 0) {
		return [...sats];
	}
	const allowed = new Set(selectedGnss);
	return sats.filter(sat => sat.length > 0 && allowed.has(sat.charAt(0)));
};


const COVER_CLASS_TO_STATUS = {
	quorum: BRDCM_STATUS_OK,
	weak: BRDCM_STATUS_SINGLE,
	held: BRDCM_STATUS_HELD,
} as const satisfies Record<BrdcmCoverClass, number>;


export const coverClassToStatus = (coverClass: BrdcmCoverClass | null | undefined): number | null => {
	if (coverClass == null) {
		return null;
	}
	return COVER_CLASS_TO_STATUS[coverClass];
};


export const indexMergesByDatetime = (merges: BrdcmMergeItem[]): Map<string, BrdcmMergeItem> => {
	const result = new Map<string, BrdcmMergeItem>();
	for (const mergeItem of merges) {
		result.set(mergeItem.current_datetime, mergeItem);
	}
	return result;
};


const buildCommonTooltipRows = (
	item: BrdcmStatsItem | undefined,
	mergeItem: BrdcmMergeItem | undefined,
	labels: BrdcmHeatmapTooltipLabels,
): HeatmapTooltipRow[] => {
	const tooltipRows: HeatmapTooltipRow[] = [];
	if (mergeItem != null) {
		tooltipRows.push({
			label: labels.mergeId,
			value: String(mergeItem.merge_id),
		});
	}
	if (item != null && item.stations_count != null) {
		tooltipRows.push({
			label: labels.stations,
			value: String(item.stations_count),
		});
	}
	if (item != null && item.quorum_count != null) {
		tooltipRows.push({
			label: labels.minimum,
			value: String(item.quorum_count),
		});
	}
	if (item != null && item.stations.length > 0) {
		tooltipRows.push({
			label: labels.ephStations,
			value: item.stations.join(', '),
		});
	}
	if (item != null && item.msm_stations.length > 0) {
		tooltipRows.push({
			label: labels.msmStations,
			value: item.msm_stations.join(', '),
		});
	}
	return tooltipRows;
};


export const classifyCoverCell = (
	item: BrdcmStatsItem | undefined,
	mergeItem: BrdcmMergeItem | undefined,
	labels: BrdcmHeatmapTooltipLabels,
): { status: number | null; tooltipRows: HeatmapTooltipRow[] } => {
	if (item == null || item.cover_class == null) {
		return {
			status: null,
			tooltipRows: buildCommonTooltipRows(item, mergeItem, labels),
		};
	}
	return {
		status: coverClassToStatus(item.cover_class),
		tooltipRows: buildCommonTooltipRows(item, mergeItem, labels),
	};
};


export const classifyMsmCell = (
	item: BrdcmStatsItem | undefined,
	mergeItem: BrdcmMergeItem | undefined,
	labels: BrdcmHeatmapTooltipLabels,
): { status: number | null; tooltipRows: HeatmapTooltipRow[] } => {
	const tooltipRows = buildCommonTooltipRows(item, mergeItem, labels);
	if (item == null || item.msm_stations.length === 0) {
		return { status: null, tooltipRows };
	}
	if (item.cover_class == null) {
		return { status: BRDCM_STATUS_MULTI, tooltipRows };
	}
	return {
		status: coverClassToStatus(item.cover_class),
		tooltipRows,
	};
};


export const collectCoverSeriesKeys = (items: BrdcmStatsItem[]): string[] => {
	const sats = new Set<string>();
	for (const item of items) {
		if (item.cover_class != null) {
			sats.add(item.satellite);
		}
	}
	return sortSatLabels(Array.from(sats));
};


export const collectMsmSeriesKeys = (items: BrdcmStatsItem[]): string[] => {
	const sats = new Set<string>();
	for (const item of items) {
		sats.add(item.satellite);
	}
	return sortSatLabels(Array.from(sats));
};


export const buildCoverChartItems = (
	items: BrdcmStatsItem[],
	merges: BrdcmMergeItem[],
	labels: BrdcmHeatmapTooltipLabels,
): BrdcmHeatmapChartPointItem[] => {
	const mergeIndex = indexMergesByDatetime(merges);
	return items
		.filter(item => item.cover_class != null)
		.map(item => {
			const classification = classifyCoverCell(item, mergeIndex.get(item.current_datetime), labels);
			return {
				current_datetime: item.current_datetime,
				satellite: item.satellite,
				status: classification.status,
				tooltipRows: classification.tooltipRows,
			};
		});
};


export const buildMsmChartItems = (
	items: BrdcmStatsItem[],
	merges: BrdcmMergeItem[],
	labels: BrdcmHeatmapTooltipLabels,
): BrdcmHeatmapChartPointItem[] => {
	const mergeIndex = indexMergesByDatetime(merges);
	return items.map(item => {
		const classification = classifyMsmCell(item, mergeIndex.get(item.current_datetime), labels);
		return {
			current_datetime: item.current_datetime,
			satellite: item.satellite,
			status: classification.status,
			tooltipRows: classification.tooltipRows,
		};
	});
};


type BrdcmColorLabels = {
	quorum: string;
	weak: string;
	held: string;
	msmNoEph?: string;
};


export const buildBrdcmCoverColorSettings = (labels: BrdcmColorLabels) => ([
	{ exactValue: BRDCM_STATUS_OK, color: HEATMAP_OK_COLOR, label: labels.quorum },
	{ exactValue: BRDCM_STATUS_HELD, color: BRDCM_HELD_COLOR, label: labels.held },
	{ exactValue: BRDCM_STATUS_SINGLE, color: HEATMAP_SINGLE_COLOR, label: labels.weak },
]);


export const buildBrdcmMsmColorSettings = (labels: BrdcmColorLabels) => ([
	{ exactValue: BRDCM_STATUS_OK, color: HEATMAP_OK_COLOR, label: labels.quorum },
	{ exactValue: BRDCM_STATUS_HELD, color: BRDCM_HELD_COLOR, label: labels.held },
	{ exactValue: BRDCM_STATUS_SINGLE, color: HEATMAP_SINGLE_COLOR, label: labels.weak },
	{ exactValue: BRDCM_STATUS_MULTI, color: HEATMAP_MULTI_COLOR, label: labels.msmNoEph },
]);
